import { Children, ReactElement, cloneElement, forwardRef } from 'react';

import { IconButton, IconButtonProps, Tooltip, TooltipProps, cn } from '@growtherapy/sprout-ui';

import { useTheme } from '../hooks';
import { Theme } from '../state';
import { VisualIndicator } from './VisualIndicator';

const DARK_THEME_CLASSNAMES =
  'bg-neutral-900 text-neutral-100 active:text-neutral-100 disabled:text-neutral-700 hover:bg-[#000000] active:bg-[#000000] disabled:hover:bg-neutral-900 rebrand:bg-transparent rebrand:border-none rebrand:text-text-inverse rebrand:hover:bg-[#AFACA5] rebrand:active:bg-[#807E79] rebrand:disabled:text-text-state-inactive-default rebrand:disabled:hover:bg-transparent';

const SIZING_AND_SPACING_CLASSNAMES =
  'min-w-12 max-w-12 min-h-12 max-h-12 rebrand:min-h-component-150 rebrand:min-w-component-150 rebrand:shadow-border-none rebrand:rounded-200-250 justify-center p-0';

const mergeButtonClassNames = (theme: Theme, shouldOverrideClassName: boolean = false, className?: string) => {
  const classJoinArgs = [{ [DARK_THEME_CLASSNAMES]: theme === Theme.DARK }, SIZING_AND_SPACING_CLASSNAMES];

  if (className && shouldOverrideClassName) {
    return cn(className, classJoinArgs);
  } else {
    return cn(classJoinArgs, className);
  }
};

type ControlPanelButtonProps = IconButtonProps & {
  /**
   * When used with MenuToggleButton, this prop is passed automatically to
   * indicate the menu's open state. It is forwarded to children of the button
   * as `isOpen`.
   */
  active?: boolean;
  showVisualIndicator?: boolean;
  containerClassName?: string;
  /**
   * If true, the class names defined and assigned specifically by this
   * component will take precedence over any `className` prop passed when being
   * merged, before being applied to the button. This is useful if the
   * `className` prop is actually coming from another component, for example for
   * the `MenuToggleButton` component in Sprout, when it renders as a
   * `ControlPanelButton`.
   */
  shouldOverrideClassName?: boolean;
  children?: ReactElement | ReactElement[] | null;
};

/** Wraps IconButton component to ensure consistent color and sizing. **/
export const ControlPanelButton = forwardRef<HTMLButtonElement, ControlPanelButtonProps>(function ControlPanelButton(
  {
    active,
    className,
    containerClassName,
    showVisualIndicator = false,
    shouldOverrideClassName = false,
    children,
    ...props
  },
  ref,
) {
  const { theme } = useTheme();
  return (
    <div className={cn('relative', containerClassName)}>
      <IconButton
        ref={ref}
        use='secondary'
        className={mergeButtonClassNames(
          theme,
          shouldOverrideClassName,
          cn('shadow-border-rebrand-neutral-800', className),
        )}
        {...props}
      />
      {showVisualIndicator && <VisualIndicator data-testid='visual-indicator' className='absolute -top-1 -right-1' />}
      {/** Forward the `active` prop to children of the button as `isOpen` */}
      {children &&
        Children.map(children, (child) => {
          if (!child) return null;
          return cloneElement(child, { isOpen: active });
        })}
    </div>
  );
});

/**
 * A thin wrapper around the Tooltip component that applies a non-zero z-index
 * to ensure the tooltip is visible above toasts.
 */
export const ControlPanelButtonTooltip = (props: TooltipProps) => {
  // NOTE: Toasts are rendered last in the DOM and thus will naturally have a
  // higher z-index than the session controls. However, they are visually
  // positioned above the session controls bar and thus do not obscure the
  // control buttons themselves except when animating in/out. But, when a
  // control button tooltip is triggered, it becomes the most relevant content
  // and thus should be visible above the toasts. Thus, we set a non-default
  // z-index for the tooltip to enable this.
  // NOTE: !w-max encourages the tooltip not to constrain its width to its c
  // child's width.
  return <Tooltip {...props} className={cn('z-[1] !w-max', props.className)} />;
};
